<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { computed } from 'vue';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import ReportsStatCards from '@/components/reports/ReportsStatCards.vue';
import BarChart from '@/components/ui/BarChart.vue';
import ToolbarIconButton from '@/components/ui/ToolbarIconButton.vue';
import IconExport from '@/components/icons/IconExport.vue';
import QueuedExportModal from '@/components/modals/QueuedExportModal.vue';
import { useReportsPayments } from '@/composables/useReportsPayments';
import { formatDate } from '@/utils/formatUtils';
import { exportPaymentsReport, type PaymentsReportMetricKey } from '@/services/reportsService';
import { useQueuedExport } from '@/composables/useQueuedExport';
import { useDateRangeWithTimezone } from '@/composables/useDateRangeWithTimezone';

const { selectedDateRange } = useDateRangeWithTimezone();

const formattedDateRange = computed<[string, string] | null>(() => {
    const range = selectedDateRange.value;
    if (!range) return null;
    return [formatDate(range[0]), formatDate(range[1])];
});

const {
    viewMode,
    activeStatId,
    stats,
    chartData,
    chartOptions,
    openBucket,
    dailyAverage,
    loading,
    rangeIncludesCurrentPeriod,
} = useReportsPayments(formattedDateRange);

const {
    exporting,
    cooldownActive,
    queuedModalVisible,
    queuedTitle,
    queuedMessage,
    run: runExport,
} = useQueuedExport({
    queuedTitle: 'Payments report export started',
    successDetail: 'Payments report CSV downloaded.',
    errorDetail: 'Unable to export the payments report. Please try again.',
});


function exportCsv() {
    const range = formattedDateRange.value;
    if (!range) return;
    const [from, to] = range;

    runExport(() => exportPaymentsReport({
        from,
        to,
        view: viewMode.value,
        metric: activeStatId.value as PaymentsReportMetricKey | null,
    }));
}
</script>

<template>
    <div class="reports-payments-tab">
        <div class="reports-tab__toolbar">
            <DateRangePicker
                v-model="selectedDateRange"
                label="Filter by Date:"
                trigger-width="350px"
                trigger-radius="10px"
            />

            <div class="reports-tab__toolbar-right">
                <ToolbarIconButton
                    ariaLabel="Export"
                    :disabled="exporting || cooldownActive"
                    @click="exportCsv"
                >
                    <IconExport />
                </ToolbarIconButton>
            </div>
        </div>

        <div class="reports-tab__stats">
            <ReportsStatCards
                :items="stats"
                :active-id="activeStatId"
                :loading="loading"
                @update:active-id="activeStatId = $event"
            />
        </div>

        <div class="reports-payments-tab__chart">
            <BarChart
                :chart-data="chartData"
                :daily-average="activeStatId ? dailyAverage : null"
                :loading="loading"
                :highlight-current-period="rangeIncludesCurrentPeriod"
                :chart-options="chartOptions"
                clickable
                @update:view-mode="viewMode = $event"
                @bar-click="openBucket"
            />
        </div>
    </div>

    <QueuedExportModal
        v-model:visible="queuedModalVisible"
        :title="queuedTitle"
        :message="queuedMessage"
    />
</template>

<style lang="scss" scoped>
.reports-payments-tab {
    @include tab-base(25px 30px 30px);
}

.reports-tab__toolbar {
    @include tab-toolbar(20px);
    margin-bottom: 25px;
}

.reports-tab__toolbar-right {
    @include tab-toolbar-right;
}

.reports-tab__stats {
    margin-bottom: 20px;
}
</style>
